import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import LanguageIcon from '@mui/icons-material/Language'
import Menu, { MenuProps } from '@mui/material/Menu'
import React, { useState } from 'react'
import { LANGUAGES_MAP } from 'src/constants/CurrencyAndLanguageConstants'
import { useCurrencyAndLanguageProvider } from 'src/context/CurrencyAndLanguageProvider'
import { Selectors } from './Selectors'

const StyledMenuBase = (props: MenuProps) => {
  const { className, ...rest } = props
  return (
    <Menu
      className={`[&_.MuiPaper-root]:rounded-[16px] [&_.MuiPaper-root]:shadow-[0_10px_40px_rgba(0,0,0,0.12)] ${className ?? ''}`.trim()}
      {...rest}
    />
  )
}

const MemoizedStyledMenu = React.memo(StyledMenuBase)

const LanguageDropdown = () => {
  const {
    currentCurrency,
    selectedCurrency,
    currentLocale,
    selectedLocale,
    handleCurrencyChange,
    handleLanguageChange,
    handleSetChanges
  } = useCurrencyAndLanguageProvider()

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [isOpen, setIsOpen] = useState(false)

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleApply = () => {
    handleSetChanges()
    handleClose()
  }

  const toggleDropdown = () => {
    setIsOpen(!isOpen)
  }

  return (
    <>
      {/* Desktop dropdown */}
      <div className="hidden md:flex">
        <button
          className="flex items-center gap-1 rounded-lg p-2 text-ink-400 transition-colors hover:bg-cream-300 hover:text-ink-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800 xl:gap-2"
          onClick={handleMenu}
        >
          <LanguageIcon className="text-xl" />
          <div className="hidden items-center gap-2 xl:flex">
            <span className="text-sm font-medium">{currentCurrency}</span>
            <span className="h-4 w-px bg-ink-100/30" />
            <span className="text-sm font-medium">
              {LANGUAGES_MAP[currentLocale].abbr}
            </span>
          </div>
          <ExpandMoreIcon
            className={`text-lg transition-transform duration-200 ${anchorEl ? 'rotate-180' : ''}`}
          />
        </button>
        <MemoizedStyledMenu
          style={{ marginTop: '10px' }}
          anchorEl={anchorEl}
          id="language-menu"
          open={Boolean(anchorEl)}
          onClose={handleClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <Selectors
            selectedCurrency={selectedCurrency}
            selectedLocale={selectedLocale}
            handleCurrencyChange={handleCurrencyChange}
            handleLanguageChange={handleLanguageChange}
            handleSetChanges={handleApply}
          />
        </MemoizedStyledMenu>
      </div>

      {/* Mobile dropdown */}
      <div className="flex w-full flex-col md:hidden">
        <button
          className="flex w-full items-center justify-between rounded-xl py-2 text-ink-700 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800"
          onClick={toggleDropdown}
        >
          <div className="flex items-center gap-3">
            <LanguageIcon className="text-xl text-ink-400" />
            <span className="text-base font-medium">{currentCurrency}</span>
            <span className="h-5 w-px bg-ink-100/30" />
            <span className="text-base font-medium">
              {LANGUAGES_MAP[currentLocale].abbr}
            </span>
          </div>
          <ExpandMoreIcon
            className={`text-lg text-ink-200 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          />
        </button>
        <div
          className={`overflow-hidden transition-all duration-300 ${isOpen ? 'mt-4 max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
        >
          <Selectors
            selectedCurrency={selectedCurrency}
            selectedLocale={selectedLocale}
            handleCurrencyChange={handleCurrencyChange}
            handleLanguageChange={handleLanguageChange}
            handleSetChanges={handleSetChanges}
            autoSet={true}
          />
        </div>
      </div>
    </>
  )
}

export default React.memo(LanguageDropdown)
